import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import type { FilterOptionType } from "./FilterOption";
import { FilterOption } from "./FilterOption";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { capitalizeWords } from "@rebuild/shared";

interface FilterTableProps {
  className?: string;
  containerClassName?: string;
  rightSection?: ReactNode;
  defaultOption?: FilterOptionType;
  selectedOption?: FilterOptionType;
  options: Array<FilterOptionType>;
  onSelectOption: (option: FilterOptionType) => void;
  scrollButton?: boolean;
}

const COMPACT_BREAKPOINT = 1100;

export const FilterTab = ({
  options,
  className,
  rightSection,
  selectedOption: _selectedOption,
  onSelectOption,
  containerClassName,
  defaultOption = { value: "All", icon: undefined, id: "All" },
  scrollButton = false,
}: FilterTableProps) => {
  const [selectedOption, setSelectedOption] = useState<FilterOptionType>(defaultOption);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    if (_selectedOption) {
      setSelectedOption(_selectedOption);
    }
  }, [_selectedOption]);

  useEffect(() => {
    const checkWidth = () => {
      setIsCompact(window.innerWidth < COMPACT_BREAKPOINT);
    };
    checkWidth();
    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  const [showScrollButton, setShowScrollButton] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const checkOverflow = () => {
    const el = scrollRef.current;
    if (el) {
      setShowScrollButton(el.scrollWidth > el.clientWidth);
      setCanScrollLeft(el.scrollLeft > 0);
    }
  };

  useEffect(() => {
    checkOverflow(); // on mount and options change
    window.addEventListener("resize", checkOverflow);

    const el = scrollRef.current;
    el?.addEventListener("scroll", checkOverflow);

    return () => {
      window.removeEventListener("resize", checkOverflow);
      el?.removeEventListener("scroll", checkOverflow);
    };
  }, [options]);

  const _onSelectOption = (option: FilterOptionType) => {
    setSelectedOption(option);
    onSelectOption(option);
  };

  const onSelectFromDropdown = (value: string) => {
    const option = options.find((o) => o.id === value);
    if (option) {
      _onSelectOption(option);
    }
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 150, behavior: "smooth" });
  };

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -150, behavior: "smooth" });
  };

  const filteredOptions = options?.filter((option) => option.value !== "");

  return (
    <div className={`relative flex flex-row items-center justify-between px-4 py-2 text-xs ${containerClassName}`}>
      {isCompact ? (
        <Select value={selectedOption.id} onValueChange={onSelectFromDropdown}>
          <SelectTrigger className="h-8 w-[180px] text-xs">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent className="border-0 !bg-white">
            {filteredOptions?.map((option) => (
              <SelectItem
                key={option.id}
                value={option.id}
                className="cursor-pointer !bg-white text-xs !text-black hover:!bg-slate-100 hover:!text-black"
              >
                {capitalizeWords(option.value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <>
          {canScrollLeft && scrollButton && (
            <div
              onClick={scrollLeft}
              className="absolute top-[6px] left-2 z-10 rounded-full border border-gray-300 bg-white p-1 shadow-lg transition-all hover:bg-gray-100"
            >
              <ChevronRight className="h-4 w-4 rotate-180 text-gray-700" />
            </div>
          )}

          {showScrollButton && scrollButton && (
            <div
              onClick={scrollRight}
              className="absolute top-[6px] right-2 z-10 rounded-full border border-gray-300 bg-white p-1 shadow-lg transition-all hover:bg-gray-100"
            >
              <ChevronRight className="h-4 w-4 text-gray-700" />
            </div>
          )}

          <div
            ref={scrollRef}
            className={`flex flex-row overflow-x-auto pr-4 ${className}`}
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {filteredOptions?.map((option, index) => (
              <div className="flex flex-row" key={`${option.value}_${index}`}>
                <FilterOption option={option} onClick={_onSelectOption} selected={selectedOption.id === option.id} />
                {index < filteredOptions.length - 1 && <div className="mx-2 my-1 border-l border-gray-300" />}
              </div>
            ))}
          </div>
        </>
      )}

      <div>{rightSection}</div>
    </div>
  );
};
